import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing ConfirmModal state consistently
 * @param {Object} options - Configuration options
 * @param {string} options.type - Type of confirmation (delete, stage-change, etc.)
 * @param {Function} options.onConfirm - Function to execute on confirmation
 * @param {Object} options.messages - Custom messages for the modal
 * @returns {Object} Modal state and handlers
 */
export function useConfirmModal(options = {}) {
  const { 
    type = 'delete', 
    onConfirm, 
    messages = {}, 
    autoCloseSuccess = false,
    skipSuccess = false 
  } = options;

  const [modalState, setModalState] = useState({
    show: false,
    state: 'pending', // 'pending' | 'success' | 'error'
    loading: false,
  });

  const [pendingData, setPendingData] = useState(null);

  const openModal = useCallback((data = null) => {
    setPendingData(data);
    setModalState({
      show: true,
      state: 'pending',
      loading: false,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      show: false,
      state: 'pending',
      loading: false,
    });
    setPendingData(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!onConfirm) return;

    setModalState(prev => ({ ...prev, loading: true }));

    try {
      const result = await onConfirm(pendingData);
      
      if (result?.success !== false) {
        if (skipSuccess) {
          closeModal();
          return result;
        }

        setModalState({
          show: true,
          state: 'success',
          loading: false,
        });

        // Automatically close the modal if autoCloseSuccess is enabled
        if (autoCloseSuccess) {
          const delay = typeof autoCloseSuccess === 'number' ? autoCloseSuccess : 1500;
          setTimeout(() => {
            closeModal();
          }, delay);
        }
      } else {
        setModalState({
          show: true,
          state: 'error',
          loading: false,
        });
      }
    } catch (error) {
      console.error(`${type} confirmation error:`, error);
      setModalState({
        show: true,
        state: 'error',
        loading: false,
      });
    }
  }, [onConfirm, pendingData, type, autoCloseSuccess, skipSuccess, closeModal]);

  const getDefaultMessages = useCallback(() => {
    const messageMap = {
      delete: {
        pending: {
          title: 'Delete Item?',
          description: 'Are you sure you want to delete this item? This action cannot be undone.',
          actionText: 'Delete',
        },
        success: {
          title: 'Item Deleted',
          description: 'The item has been successfully deleted.',
          actionText: 'Done',
        },
        error: {
          title: 'Delete Failed',
          description: 'Failed to delete the item. Please try again or contact support.',
          actionText: 'Retry',
        },
      },
      'stage-change': {
        pending: {
          title: 'Change Stage?',
          description: 'Are you sure you want to change the stage? This action will update the progression.',
          actionText: 'Change Stage',
        },
        success: {
          title: 'Stage Changed Successfully',
          description: 'The stage has been successfully updated.',
          actionText: 'Done',
        },
        error: {
          title: 'Stage Change Failed',
          description: 'Failed to update the stage. Please try again or contact support.',
          actionText: 'Retry',
        },
      },
    };

    return messageMap[type] || messageMap.delete;
  }, [type]);

  const mergedMessages = useMemo(() => ({
    ...getDefaultMessages(),
    ...messages,
  }), [getDefaultMessages, messages]);

  const updateMessages = useCallback((newMessages) => {
    // This function allows updating messages dynamically if needed locally
  }, []);

  return {
    modalState,
    pendingData,
    openModal,
    closeModal,
    handleConfirm,
    messages: mergedMessages,
    updateMessages,
  };
}